'use strict';

import { Map as OlMap, View } from 'ol';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import OSM from 'ol/source/OSM';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import { fromLonLat, toLonLat } from 'ol/proj.js';
import { boundingExtent } from 'ol/extent';
import { defaults as defaultControls } from 'ol/control.js';
import LayerSwitcher from 'ol-layerswitcher';
import { Style, Fill, Circle, Stroke } from 'ol/style.js';

import Autocomplete from 'bootstrap5-autocomplete';

import { TARGET_LOC, PA_BOUNDS } from './src/config.js';
import { styleForMapPin } from './src/lib/pin-marker-style.js';
import { findParkSearchMatch } from './src/parks/park-search.js';
import { createDraggablePopup } from './src/lib/draggable-popup.js';
import { createPaResearchLayers } from './src/layers/pa-research-layers.js';
import { mountCandidatePopup, formatCoords, copyCoords } from './src/popups/candidate-popup.js';
import { mountParkPopup, parkFromFeature } from './src/popups/park-popup.js';
import { mountPinPopup } from './src/popups/pin-popup.js';
import { formatAreaSqM } from './src/lib/units.js';
import { CandidateReviewStore } from './src/review/candidate-review-store.js';
import { MapPinStore } from './src/pins/map-pin-store.js';
import { parseBulkPinText, parseCoordPair } from './src/pins/pin-import.js';
import {
    applyCommunityRejectionsToCandidate,
    callSignStorageKey,
    fetchCommunityRejections,
    rejectionsByCandidateId,
    submitCommunityRejection,
} from './src/review/community-rejections.js';

const LOC_ID = TARGET_LOC;
const paExtent = boundingExtent([
    fromLonLat([PA_BOUNDS[0], PA_BOUNDS[1]]),
    fromLonLat([PA_BOUNDS[2], PA_BOUNDS[3]]),
]);

const reviewStore = new CandidateReviewStore(LOC_ID);
const pinStore = new MapPinStore(LOC_ID);

const selectionState = { candidateId: null };

const filterState = {
    confidence: 'all',
    reviewStatus: 'all',
    hideRejected: false,
    getSelectedCandidateId() {
        return selectionState.candidateId;
    },
    passesFilter(props) {
        if (!props?.candidateId) {
            return true;
        }
        if (this.hideRejected && props.reviewStatus === 'rejected') {
            return false;
        }
        if (this.confidence !== 'all' && props.confidence !== this.confidence) {
            return false;
        }
        if (this.reviewStatus !== 'all' && props.reviewStatus !== this.reviewStatus) {
            return false;
        }
        return true;
    },
};

const research = createPaResearchLayers('EPSG:3857', filterState);

const map = new OlMap({
    target: 'paOverlapMap',
    layers: [
        new TileLayer({ source: new OSM(), title: 'OpenStreetMap', type: 'base' }),
        research.group,
    ],
    view: new View({
        center: fromLonLat([-77.2098, 40.5908]),
        zoom: 7,
        extent: paExtent,
        minZoom: 6,
        maxZoom: 18,
        constrainOnlyCenter: false,
    }),
    controls: defaultControls({ attribution: true }),
});

map.addControl(new LayerSwitcher({
    startActive: false,
    activationMode: 'click',
    groupSelectStyle: 'children',
    tipLabel: 'Layers',
}));

const coordMarkerSource = new VectorSource();
map.addLayer(new VectorLayer({
    source: coordMarkerSource,
    zIndex: 40,
    style: new Style({
        image: new Circle({
            radius: 7,
            fill: new Fill({ color: 'rgba(255, 204, 0, 0.95)' }),
            stroke: new Stroke({ color: '#222', width: 2 }),
        }),
    }),
}));

const pinSource = new VectorSource();
const pinLayer = new VectorLayer({
    source: pinSource,
    zIndex: 42,
    style: (feature) => styleForMapPin(feature, openPinId),
});
map.addLayer(pinLayer);

const popupElement = document.getElementById('candidatePopup');
const draggablePopup = createDraggablePopup(popupElement, map);

const statusEl = document.getElementById('status');
const listEl = document.getElementById('candidateList');
const searchInput = document.getElementById('searchInput');
const confidenceFilter = document.getElementById('confidenceFilter');
const reviewFilter = document.getElementById('reviewFilter');
const hideRejectedFilter = document.getElementById('hideRejectedFilter');
const reviewerNameInput = document.getElementById('reviewerName');
const callSignInput = document.getElementById('callSignInput');
const exportReviewBtn = document.getElementById('exportReviewBtn');
const exportConfirmedBtn = document.getElementById('exportConfirmedBtn');
const exportRejectedBtn = document.getElementById('exportRejectedBtn');
const importReviewInput = document.getElementById('importReviewInput');
const hoverLabelEl = document.getElementById('hoverLabel');
const coordPickBtn = document.getElementById('coordPickBtn');
const dropPinBtn = document.getElementById('dropPinBtn');
const toggleBoundariesBtn = document.getElementById('toggleBoundariesBtn');
const pinListEl = document.getElementById('pinList');
const pinCountBadge = document.getElementById('pinCountBadge');
const pinEntryPanel = document.getElementById('pinEntryPanel');
const pinIdleHint = document.getElementById('pinIdleHint');
const pinLatInput = document.getElementById('pinLatInput');
const pinLonInput = document.getElementById('pinLonInput');
const pinEntryNoteInput = document.getElementById('pinEntryNoteInput');
const addSinglePinBtn = document.getElementById('addSinglePinBtn');
const pinBulkInput = document.getElementById('pinBulkInput');
const addBulkPinsBtn = document.getElementById('addBulkPinsBtn');
const pinCsvInput = document.getElementById('pinCsvInput');
const pinCsvPreview = document.getElementById('pinCsvPreview');
const addCsvPinsBtn = document.getElementById('addCsvPinsBtn');
const pinImportFeedback = document.getElementById('pinImportFeedback');
const parkSearchInput = document.getElementById('parkSearchInput');
const parkSearchGoBtn = document.getElementById('parkSearchGoBtn');

/** @type {object[]} */
let parkSearchItems = [];

let coordPickMode = false;
let pinDropMode = false;
let boundariesVisible = false;
/** @type {string|null} */
let openPinId = null;
/** @type {Map<string, object[]>} */
let communityRejectionMap = new Map();

/** @type {Array<{ candidate: object, row: HTMLElement }>} */
let listEntries = [];
let activeCandidateId = null;
/** @type {object|null} */
let openCandidate = null;
let openPark = null;

function setStatus(message, isError = false) {
    statusEl.textContent = message;
    statusEl.classList.toggle('error', isError);
}

function setHoverLabel(text) {
    if (!hoverLabelEl) {
        return;
    }
    if (text) {
        hoverLabelEl.textContent = text;
        hoverLabelEl.classList.remove('d-none');
    } else {
        hoverLabelEl.textContent = '';
        hoverLabelEl.classList.add('d-none');
    }
}

function closePopup() {
    draggablePopup.hide();
    openCandidate = null;
    openPark = null;
    openPinId = null;
    selectionState.candidateId = null;
    research.refreshCandidateStyles();
    pinLayer.changed();
}

function candidateFromFeature(feature) {
    const props = feature.getProperties();
    const { geometry, ...candidate } = props;
    return applyCandidateWithReviews(reviewStore.applyCandidate(candidate));
}

function collectCandidates() {
    const all = [];
    for (const feature of research.sources.candidate2.getFeatures()) {
        if (feature.get('featureKind') === 'setup-point') {
            all.push(candidateFromFeature(feature));
        }
    }
    for (const feature of research.sources.candidateN.getFeatures()) {
        if (feature.get('featureKind') === 'setup-point') {
            all.push(candidateFromFeature(feature));
        }
    }

    const seen = new Set();
    return all.filter((candidate) => {
        if (seen.has(candidate.candidateId)) {
            return false;
        }
        seen.add(candidate.candidateId);
        return true;
    });
}

function syncCandidateFeatures(candidateId, reviewed) {
    for (const source of [research.sources.candidate2, research.sources.candidateN]) {
        for (const feature of source.getFeatures()) {
            if (feature.get('candidateId') !== candidateId) {
                continue;
            }

            const featureKind = feature.get('featureKind');
            const candidateKind = feature.get('candidateKind');
            const preserved = {
                featureKind,
                candidateKind,
            };

            if (featureKind === 'setup-point' && reviewed.setupPoint) {
                feature.getGeometry()?.setCoordinates(
                    fromLonLat([reviewed.setupPoint.lon, reviewed.setupPoint.lat]),
                );
            }

            const { geometry, ...props } = reviewed;
            feature.setProperties({ ...props, ...preserved }, true);
        }
    }
}

function reviewPillHtml(candidate) {
    const status = candidate.reviewStatus;
    if (!status || status === 'unreviewed') {
        return '';
    }
    const count = candidate.communityRejectionCount;
    const label = status === 'rejected' && count
        ? `rejected (${count})`
        : status.replace(/_/g, ' ');
    return `<span class="review-pill review-${status}">${label}</span>`;
}

function updateListRow(entry) {
    const candidate = entry.candidate;
    const kind = candidate.n >= 3 || candidate.candidateKind === 'nfer' ? '3-fer+' : '2-fer';
    const areaLabel = candidate.overlapAreaSqM
        ? formatAreaSqM(candidate.overlapAreaSqM)
        : null;

    entry.row.innerHTML = `
        <span class="overlap-badge ${kind === '3-fer+' ? 'badge-nfer' : ''}">${kind}</span>
        <span class="confidence-pill confidence-${candidate.confidence}">${candidate.confidence}</span>
        ${reviewPillHtml(candidate)}
        <div class="overlap-parks">${candidate.refs.join(' + ')}</div>
        <div class="overlap-titles">${candidate.refTitles.join(' / ')}</div>
        ${areaLabel ? `<div class="overlap-area">${areaLabel} · ${candidate.type}</div>` : `<div class="overlap-area">${candidate.type}</div>`}
    `;
}

function applyReviewsToAll() {
    for (const entry of listEntries) {
        entry.candidate = applyCandidateWithReviews({
            ...entry.candidate,
            originalSetupPoint: entry.candidate.originalSetupPoint || entry.candidate.setupPoint,
        });
        syncCandidateFeatures(entry.candidate.candidateId, entry.candidate);
        updateListRow(entry);
    }
    refreshListVisibility();
}

function applyCandidateWithReviews(candidate) {
    const reviewed = reviewStore.applyCandidate({
        ...candidate,
        originalSetupPoint: candidate.originalSetupPoint || candidate.setupPoint,
    });
    const community = communityRejectionMap.get(reviewed.candidateId);
    return applyCommunityRejectionsToCandidate(reviewed, community);
}

async function refreshCommunityRejections() {
    const rows = await fetchCommunityRejections(LOC_ID);
    communityRejectionMap = rejectionsByCandidateId(rows);
    applyReviewsToAll();
}

function persistIdentityFields() {
    if (typeof localStorage === 'undefined') {
        return;
    }
    const name = reviewerNameInput?.value?.trim();
    const callSign = callSignInput?.value?.trim();
    if (name) {
        reviewStore.setReviewer(name);
    }
    if (callSign) {
        localStorage.setItem(callSignStorageKey(LOC_ID), callSign.toUpperCase());
    }
}

function loadIdentityFields() {
    if (typeof localStorage !== 'undefined') {
        const callSign = localStorage.getItem(callSignStorageKey(LOC_ID));
        if (callSign && callSignInput) {
            callSignInput.value = callSign;
        }
    }
    if (reviewStore.reviewer && reviewerNameInput) {
        reviewerNameInput.value = reviewStore.reviewer;
    }
}

async function handleReviewSave(candidateId, patch) {
    const entry = listEntries.find((row) => row.candidate.candidateId === candidateId);
    if (!entry) {
        return;
    }

    if (patch.reviewStatus === 'rejected' && patch.submitCommunity) {
        const callSign = patch.callSign?.trim().toUpperCase();
        const reviewerName = patch.reviewerName?.trim();
        if (!callSign || !reviewerName) {
            setStatus('Call sign and name are required to submit a shared rejection.', true);
            return;
        }

        persistIdentityFields();

        try {
            const candidate = entry.candidate;
            await submitCommunityRejection(LOC_ID, {
                candidateId,
                callSign,
                reviewerName,
                notes: patch.notes || '',
                refs: candidate.refs || [],
                refTitles: candidate.refTitles || [],
                setupPoint: candidate.setupPoint || null,
            });
            await refreshCommunityRejections();
            const updatedEntry = listEntries.find((row) => row.candidate.candidateId === candidateId);
            if (updatedEntry?.candidate.setupPoint && openCandidate?.candidateId === candidateId) {
                showCandidatePopup(updatedEntry.candidate, fromLonLat([
                    updatedEntry.candidate.setupPoint.lon,
                    updatedEntry.candidate.setupPoint.lat,
                ]));
            }
            setStatus(`Submitted rejection for ${candidateId} (${callSign}) — visible to all users`);
            return;
        } catch (error) {
            setStatus(`Could not submit rejection: ${error.message}`, true);
            return;
        }
    } else if (patch.reviewStatus === 'unreviewed' && !patch.notes) {
        reviewStore.entries.delete(candidateId);
        reviewStore.saveToLocalStorage();
    } else {
        const payload = {
            candidateId,
            reviewStatus: patch.reviewStatus || entry.candidate.reviewStatus || 'unreviewed',
            notes: patch.notes ?? '',
        };
        if (patch.setupPointOverride === null) {
            payload.setupPointOverride = undefined;
            const existing = reviewStore.getEntry(candidateId);
            if (existing) {
                delete existing.setupPointOverride;
            }
        } else if (patch.setupPointOverride) {
            payload.setupPointOverride = patch.setupPointOverride;
        }
        reviewStore.upsert(candidateId, payload);
    }

    entry.candidate = applyCandidateWithReviews({
        ...entry.candidate,
        originalSetupPoint: entry.candidate.originalSetupPoint || entry.candidate.setupPoint,
    });
    syncCandidateFeatures(candidateId, entry.candidate);
    updateListRow(entry);
    refreshListVisibility();

    if (openCandidate?.candidateId === candidateId) {
        const reviewed = {
            ...entry.candidate,
            reviewEntry: reviewStore.getEntry(candidateId),
        };
        openCandidate = reviewed;
        showCandidatePopup(reviewed, fromLonLat([
            reviewed.setupPoint.lon,
            reviewed.setupPoint.lat,
        ]));
    }

    setStatus(`Saved review for ${candidateId} (${entry.candidate.reviewStatus})`);
}

function showCandidatePopup(candidate, coordinate) {
    openCandidate = candidate;
    openPark = null;
    openPinId = null;
    selectionState.candidateId = candidate.candidateId;
    research.refreshCandidateStyles();
    pinLayer.changed();
    const reviewed = {
        ...candidate,
        reviewEntry: reviewStore.getEntry(candidate.candidateId),
    };

    mountCandidatePopup(popupElement, reviewed, {
        onClose: closePopup,
        onCopy: (text) => setStatus(`Copied ${text}`),
        onReviewSave: (patch) => handleReviewSave(candidate.candidateId, patch),
        defaultCallSign: callSignInput?.value?.trim() || '',
        defaultReviewerName: reviewerNameInput?.value?.trim() || reviewStore.reviewer || '',
    });
    draggablePopup.placeAtCoordinate(coordinate);
}

function isParkLayer(layer) {
    return research.parkLayers.includes(layer);
}

function parkAtPixel(pixel) {
    let found = null;
    map.forEachFeatureAtPixel(pixel, (feature, layer) => {
        if (found || !isParkLayer(layer)) {
            return;
        }
        const park = parkFromFeature(feature);
        if (park.ref || park.title) {
            found = { feature, park };
        }
    }, { hitTolerance: 6 });
    return found;
}

function showParkPopup(park, coordinate) {
    openPark = park;
    openCandidate = null;
    openPinId = null;
    mountParkPopup(popupElement, park, { onClose: closePopup });
    draggablePopup.placeAtCoordinate(coordinate);
}

function syncPinsToMap() {
    pinSource.clear();
    for (const pin of pinStore.getAll()) {
        const feature = new Feature({
            geometry: new Point(fromLonLat([pin.lon, pin.lat])),
            pinId: pin.id,
            pinNote: pin.note,
        });
        pinSource.addFeature(feature);
    }
    pinLayer.changed();
}

function showPinImportFeedback(message, isError = false) {
    if (!pinImportFeedback) {
        setStatus(message, isError);
        return;
    }
    pinImportFeedback.textContent = message;
    pinImportFeedback.classList.toggle('text-danger', isError);
    pinImportFeedback.classList.toggle('text-success', !isError);
    pinImportFeedback.classList.remove('d-none');
    setStatus(message, isError);
}

function clearPinImportFeedback() {
    pinImportFeedback?.classList.add('d-none');
}

function refreshPinsUI() {
    syncPinsToMap();
    renderPinList();
}

function focusPinsOnMap(pins) {
    if (!pins.length) {
        return;
    }
    if (pins.length === 1) {
        const pin = pins[0];
        map.getView().animate({
            center: fromLonLat([pin.lon, pin.lat]),
            zoom: Math.max(map.getView().getZoom(), 13),
            duration: 400,
        });
        showPinPopup(pin, fromLonLat([pin.lon, pin.lat]));
        return;
    }

    const extent = boundingExtent(pins.map((pin) => fromLonLat([pin.lon, pin.lat])));
    map.getView().fit(extent, {
        padding: [50, 380, 50, 50],
        maxZoom: 14,
        duration: 400,
    });
}

function importPinRows(rows, errors = []) {
    const added = pinStore.addMany(rows);
    refreshPinsUI();

    if (!added.length) {
        const detail = errors.length ? ` ${errors[0]}` : '';
        showPinImportFeedback(`No pins imported.${detail}`, true);
        return added;
    }

    let message = `Added ${added.length} pin${added.length === 1 ? '' : 's'} (local only).`;
    if (errors.length) {
        message += ` ${errors.length} line${errors.length === 1 ? '' : 's'} skipped.`;
    }
    showPinImportFeedback(message, false);
    focusPinsOnMap(added);
    return added;
}

function renderPinList() {
    const pins = pinStore.getAll();
    if (pinCountBadge) {
        pinCountBadge.textContent = String(pins.length);
    }

    if (!pinListEl) {
        return;
    }

    if (!pins.length) {
        pinListEl.innerHTML = '<div class="small text-muted">No pins yet.</div>';
        return;
    }

    pinListEl.innerHTML = pins.map((pin) => {
        const preview = pin.note
            ? pin.note.replace(/</g, '&lt;').replace(/>/g, '&gt;')
            : '<em>No note</em>';
        const coords = formatCoords(pin.lat, pin.lon);
        return `
            <button type="button" class="list-group-item list-group-item-action pin-list-item${openPinId === pin.id ? ' active' : ''}"
                data-pin-id="${pin.id}">
                <div class="pin-note-preview">${preview}</div>
                <div class="pin-coords">${coords}</div>
            </button>
        `;
    }).join('');

    for (const button of pinListEl.querySelectorAll('[data-pin-id]')) {
        button.addEventListener('click', () => {
            const pinId = button.getAttribute('data-pin-id');
            const pin = pinStore.get(pinId);
            if (!pin) {
                return;
            }
            map.getView().animate({
                center: fromLonLat([pin.lon, pin.lat]),
                zoom: Math.max(map.getView().getZoom(), 13),
                duration: 400,
            });
            showPinPopup(pin, fromLonLat([pin.lon, pin.lat]));
        });
    }
}

function showPinPopup(pin, coordinate) {
    openCandidate = null;
    openPark = null;
    openPinId = pin.id;
    selectionState.candidateId = null;
    research.refreshCandidateStyles();
    pinLayer.changed();
    renderPinList();

    mountPinPopup(popupElement, pin, {
        onSave: (note) => {
            const updated = pinStore.update(pin.id, { note });
            if (!updated) {
                return;
            }
            syncPinsToMap();
            showPinPopup(updated, coordinate);
            setStatus('Saved pin note (local only)');
        },
        onDelete: () => {
            pinStore.remove(pin.id);
            syncPinsToMap();
            renderPinList();
            closePopup();
            setStatus('Deleted pin');
        },
        onCopy: (text) => setStatus(`Copied ${text}`),
        onClose: closePopup,
    });
    draggablePopup.placeAtCoordinate(coordinate);
}

function pinAtPixel(pixel) {
    let found = null;
    map.forEachFeatureAtPixel(pixel, (feature, layer) => {
        if (found || layer !== pinLayer) {
            return;
        }
        const pinId = feature.get('pinId');
        const pin = pinStore.get(pinId);
        if (pin) {
            found = { feature, pin };
        }
    }, { hitTolerance: 14 });
    return found;
}

function zoomToPark(park, coordinate) {
    highlightCandidate(null);
    if (park.lat != null && park.lon != null) {
        map.getView().animate({
            center: fromLonLat([park.lon, park.lat]),
            zoom: Math.max(map.getView().getZoom(), 12),
            duration: 400,
        });
    }
    showParkPopup(park, coordinate);
}

function buildParkSearchCatalog() {
    const items = [];
    for (const feature of research.layers.pota.getSource().getFeatures()) {
        const park = parkFromFeature(feature);
        if (park.lat == null || park.lon == null) {
            continue;
        }
        items.push({
            label: park.ref ? `${park.ref} — ${park.title}` : park.title,
            value: park.ref || park.title,
            ref: park.ref,
            title: park.title,
            grid: park.grid || '',
            lat: park.lat,
            lon: park.lon,
        });
    }
    items.sort((a, b) => a.label.localeCompare(b.label));
    parkSearchItems = items;
    return items;
}

function goToParkSearchItem(item) {
    if (!item || item.lat == null || item.lon == null) {
        setStatus('Park not found — try a POTA ref like US-1327', true);
        return;
    }

    closePopup();
    highlightCandidate(null);
    selectionState.candidateId = null;
    research.refreshCandidateStyles();

    const coordinate = fromLonLat([item.lon, item.lat]);
    map.getView().animate({
        center: coordinate,
        zoom: Math.max(map.getView().getZoom(), 12),
        duration: 400,
    });

    showParkPopup({
        ref: item.ref || item.value,
        title: item.title || item.label,
        grid: item.grid || null,
        lat: item.lat,
        lon: item.lon,
    }, coordinate);
    setStatus(`Zoomed to ${item.label}`);
}

function initParkSearch() {
    const items = buildParkSearchCatalog();
    if (!parkSearchInput || !items.length) {
        return;
    }

    Autocomplete.init('#parkSearchInput', {
        items,
        labelField: 'label',
        valueField: 'value',
        searchFields: ['label', 'ref', 'title', 'grid', 'value'],
        highlightTyped: true,
        fuzzy: true,
        fixed: true,
        suggestionsThreshold: 1,
        maximumItems: 15,
        onSelectItem: (item) => {
            goToParkSearchItem(item);
        },
    });

    parkSearchGoBtn?.addEventListener('click', () => {
        const query = parkSearchInput?.value || '';
        const match = findParkSearchMatch(parkSearchItems, query);
        if (!match) {
            setStatus(`No park found for "${query.trim()}"`, true);
            return;
        }
        goToParkSearchItem(match);
    });
}

function candidateSearchText(candidate) {
    return [
        candidate.candidateId,
        ...(candidate.refs || []),
        ...(candidate.refTitles || []),
        candidate.type,
        candidate.reviewStatus,
        candidate.reviewNotes,
    ].join(' ').toLowerCase();
}

function passesListFilters(candidate) {
    if (!filterState.passesFilter(candidate)) {
        return false;
    }
    const q = searchInput?.value?.trim().toLowerCase();
    if (!q) {
        return true;
    }
    return candidateSearchText(candidate).includes(q);
}

function refreshListVisibility() {
    for (const entry of listEntries) {
        entry.row.classList.toggle('d-none', !passesListFilters(entry.candidate));
    }
    research.refreshCandidateStyles();
}

function highlightCandidate(candidateId) {
    activeCandidateId = candidateId;
    for (const entry of listEntries) {
        entry.row.classList.toggle('active', entry.candidate.candidateId === candidateId);
    }
    research.layers.candidates2.changed();
    research.layers.candidatesN.changed();
}

function findOverlapFeature(candidateId) {
    for (const source of [research.sources.candidate2, research.sources.candidateN]) {
        for (const feature of source.getFeatures()) {
            if (feature.get('candidateId') === candidateId && feature.get('featureKind') === 'overlap') {
                return feature;
            }
        }
    }
    return null;
}

function zoomToCandidate(candidate) {
    highlightCandidate(candidate.candidateId);

    const overlapFeature = findOverlapFeature(candidate.candidateId);
    const overlapGeom = overlapFeature?.getGeometry();

    if (overlapGeom) {
        map.getView().fit(overlapGeom.getExtent(), {
            padding: [80, 400, 80, 80],
            maxZoom: 16,
            duration: 600,
        });
    } else if (candidate.setupPoint) {
        map.getView().animate({
            center: fromLonLat([candidate.setupPoint.lon, candidate.setupPoint.lat]),
            zoom: 14,
            duration: 600,
        });
    }

    const popupCoord = candidate.setupPoint
        ? fromLonLat([candidate.setupPoint.lon, candidate.setupPoint.lat])
        : overlapGeom
            ? map.getView().getCenter()
            : undefined;

    if (popupCoord) {
        showCandidatePopup(candidate, popupCoord);
    }
}

function createListRow(candidate) {
    const row = document.createElement('button');
    row.type = 'button';
    row.className = 'list-group-item list-group-item-action overlap-item';
    updateListRow({ candidate, row });

    row.addEventListener('click', () => zoomToCandidate(candidate));
    row.addEventListener('mouseenter', () => {
        highlightCandidate(candidate.candidateId);
        setHoverLabel(`${candidate.refs.join(' + ')} — ${candidate.candidateId}`);
    });
    row.addEventListener('mouseleave', () => setHoverLabel(''));

    return row;
}

function buildCandidateList(candidates) {
    listEl.innerHTML = '';
    listEntries = [];

    const sorted = [...candidates].sort((a, b) => {
        const scoreA = a.planningScore ?? a.overlapAreaSqM ?? 0;
        const scoreB = b.planningScore ?? b.overlapAreaSqM ?? 0;
        return scoreB - scoreA;
    });

    for (const candidate of sorted) {
        const reviewed = applyCandidateWithReviews({
            ...candidate,
            originalSetupPoint: candidate.setupPoint,
        });
        const row = createListRow(reviewed);
        listEl.appendChild(row);
        listEntries.push({ candidate: reviewed, row });
        syncCandidateFeatures(reviewed.candidateId, reviewed);
    }

    refreshListVisibility();
}

function isCandidateLayer(layer) {
    return layer === research.layers.candidates2 || layer === research.layers.candidatesN;
}

function setCoordPickMode(enabled) {
    coordPickMode = enabled;
    if (enabled) {
        pinDropMode = false;
        dropPinBtn?.classList.remove('active');
        dropPinBtn?.setAttribute('aria-pressed', 'false');
        document.body.classList.remove('pa-pin-drop-mode');
    }
    coordPickBtn?.classList.toggle('active', enabled);
    coordPickBtn?.setAttribute('aria-pressed', enabled ? 'true' : 'false');
    document.body.classList.toggle('pa-coord-pick-mode', enabled);
    if (!enabled) {
        coordMarkerSource.clear();
        if (!draggablePopup.isVisible()) {
            setHoverLabel('');
        }
        return;
    }
    setStatus('Coord pick mode: click the map to copy lat, lon. Hover for preview.');
}

function setPinDropMode(enabled) {
    pinDropMode = enabled;
    if (enabled) {
        coordPickMode = false;
        coordPickBtn?.classList.remove('active');
        coordPickBtn?.setAttribute('aria-pressed', 'false');
        document.body.classList.remove('pa-coord-pick-mode');
        coordMarkerSource.clear();
        clearPinImportFeedback();
    }
    dropPinBtn?.classList.toggle('active', enabled);
    dropPinBtn?.setAttribute('aria-pressed', enabled ? 'true' : 'false');
    document.body.classList.toggle('pa-pin-drop-mode', enabled);
    pinEntryPanel?.classList.toggle('d-none', !enabled);
    pinIdleHint?.classList.toggle('d-none', enabled);
    if (enabled) {
        setStatus('Pin mode: click the map or use the add-pin panel in the sidebar.');
        pinEntryPanel?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        return;
    }
    if (!draggablePopup.isVisible()) {
        setHoverLabel('');
    }
}

function setBoundariesVisible(visible) {
    boundariesVisible = visible;
    research.layers.potaParkBoundaries.setVisible(visible);
    research.layers.stateGamelandBoundaries.setVisible(visible);
    toggleBoundariesBtn?.classList.toggle('active', visible);
    toggleBoundariesBtn?.setAttribute('aria-pressed', visible ? 'true' : 'false');
}

function coordsFromEvent(evt) {
    const [lon, lat] = toLonLat(evt.coordinate);
    return { lat, lon };
}

async function handleCoordPickClick(evt) {
    const { lat, lon } = coordsFromEvent(evt);
    coordMarkerSource.clear();
    coordMarkerSource.addFeature(new Feature(new Point(evt.coordinate)));

    try {
        const copied = await copyCoords(lat, lon);
        setStatus(`Copied ${copied} — click again for another point`);
    } catch {
        setStatus(`${formatCoords(lat, lon)} — click again for another point`);
    }
    setHoverLabel(`${formatCoords(lat, lon)} (copied)`);
}

async function handlePinDropClick(evt) {
    const existing = pinAtPixel(evt.pixel);
    if (existing) {
        showPinPopup(existing.pin, evt.coordinate);
        return;
    }

    try {
        const { lat, lon } = coordsFromEvent(evt);
        const pin = pinStore.add(lat, lon, '');
        if (!pin) {
            setStatus('Could not add pin — invalid coordinates', true);
            return;
        }
        refreshPinsUI();
        showPinPopup(pin, evt.coordinate);
        setStatus('Pin dropped — add a note and save (local only)');
    } catch (error) {
        console.error(error);
        setStatus(`Could not add pin: ${error.message}`, true);
    }
}

coordPickBtn?.addEventListener('click', () => {
    setCoordPickMode(!coordPickMode);
});

dropPinBtn?.addEventListener('click', () => {
    setPinDropMode(!pinDropMode);
});

addSinglePinBtn?.addEventListener('click', () => {
    const lat = pinLatInput?.value;
    const lon = pinLonInput?.value;
    const note = pinEntryNoteInput?.value?.trim() || '';

    if (!parseCoordPair(lat, lon)) {
        showPinImportFeedback('Enter valid latitude and longitude.', true);
        return;
    }

    try {
        const added = importPinRows([{ lat, lon, note }]);
        if (!added.length) {
            return;
        }
        if (pinLatInput) pinLatInput.value = '';
        if (pinLonInput) pinLonInput.value = '';
        if (pinEntryNoteInput) pinEntryNoteInput.value = '';
    } catch (error) {
        console.error(error);
        showPinImportFeedback(`Could not add pin: ${error.message}`, true);
    }
});

addBulkPinsBtn?.addEventListener('click', () => {
    try {
        const { pins, errors } = parseBulkPinText(pinBulkInput?.value || '');
        importPinRows(pins, errors);
        if (pins.length && pinBulkInput) {
            pinBulkInput.value = '';
        }
    } catch (error) {
        console.error(error);
        showPinImportFeedback(`Could not add pins: ${error.message}`, true);
    }
});

pinCsvInput?.addEventListener('change', async () => {
    const file = pinCsvInput.files?.[0];
    if (!file) {
        return;
    }
    try {
        const text = await file.text();
        if (pinCsvPreview) {
            pinCsvPreview.value = text;
        }
        showPinImportFeedback(`Loaded ${file.name} — review and click Import CSV.`, false);
    } catch (error) {
        showPinImportFeedback(`Could not read file: ${error.message}`, true);
    }
    pinCsvInput.value = '';
});

pinCsvPreview?.addEventListener('input', () => {
    clearPinImportFeedback();
});

addCsvPinsBtn?.addEventListener('click', () => {
    const { pins, errors } = parseBulkPinText(pinCsvPreview?.value || '');
    importPinRows(pins, errors);
    if (pins.length && pinCsvPreview) {
        pinCsvPreview.value = '';
    }
});

toggleBoundariesBtn?.addEventListener('click', () => {
    setBoundariesVisible(!boundariesVisible);
});

function featureCandidateProps(feature) {
    if (!feature.get('candidateId')) {
        return null;
    }
    return candidateFromFeature(feature);
}

map.on('pointermove', (evt) => {
    if (evt.dragging) {
        return;
    }

    const target = map.getTargetElement();

    if (coordPickMode) {
        target.style.cursor = 'crosshair';
        const { lat, lon } = coordsFromEvent(evt);
        setHoverLabel(`${formatCoords(lat, lon)} — click to copy`);
        return;
    }

    if (pinDropMode) {
        target.style.cursor = 'crosshair';
        const pinHit = pinAtPixel(evt.pixel);
        if (pinHit) {
            const label = pinHit.pin.note
                ? pinHit.pin.note.slice(0, 80)
                : 'Map pin (no note)';
            setHoverLabel(`${label} — click to edit`);
            return;
        }
        const { lat, lon } = coordsFromEvent(evt);
        setHoverLabel(`${formatCoords(lat, lon)} — click to drop pin`);
        return;
    }

    let foundCandidate = null;

    map.forEachFeatureAtPixel(evt.pixel, (feature, layer) => {
        if (foundCandidate || !isCandidateLayer(layer)) {
            return;
        }
        const props = featureCandidateProps(feature);
        if (props && filterState.passesFilter(props)) {
            foundCandidate = props;
        }
    }, { hitTolerance: 8 });

    if (foundCandidate) {
        target.style.cursor = 'pointer';
        highlightCandidate(foundCandidate.candidateId);
        setHoverLabel(`${foundCandidate.refs?.join(' + ')} — ${foundCandidate.candidateId}`);
        return;
    }

    const foundPark = parkAtPixel(evt.pixel);
    if (foundPark) {
        target.style.cursor = 'pointer';
        const label = foundPark.park.ref
            ? `${foundPark.park.ref} — ${foundPark.park.title}`
            : foundPark.park.title;
        setHoverLabel(label);
        return;
    }

    const pinHit = pinAtPixel(evt.pixel);
    if (pinHit) {
        target.style.cursor = 'pointer';
        const label = pinHit.pin.note
            ? pinHit.pin.note.slice(0, 80)
            : 'Map pin (no note)';
        setHoverLabel(label);
        return;
    }

    target.style.cursor = '';
    if (!draggablePopup.isVisible()) {
        setHoverLabel('');
    }
});

map.on('click', async (evt) => {
    if (coordPickMode) {
        await handleCoordPickClick(evt);
        return;
    }

    if (pinDropMode) {
        await handlePinDropClick(evt);
        return;
    }

    let clicked = null;
    map.forEachFeatureAtPixel(evt.pixel, (feature, layer) => {
        if (clicked || !isCandidateLayer(layer)) {
            return;
        }
        const props = featureCandidateProps(feature);
        if (props && filterState.passesFilter(props)) {
            clicked = props;
        }
    }, { hitTolerance: 8 });

    if (clicked) {
        zoomToCandidate(clicked);
        return;
    }

    const pinHit = pinAtPixel(evt.pixel);
    if (pinHit) {
        selectionState.candidateId = null;
        research.refreshCandidateStyles();
        showPinPopup(pinHit.pin, evt.coordinate);
        return;
    }

    const parkHit = parkAtPixel(evt.pixel);
    if (parkHit) {
        selectionState.candidateId = null;
        research.refreshCandidateStyles();
        zoomToPark(parkHit.park, evt.coordinate);
        return;
    }

    closePopup();
});

confidenceFilter?.addEventListener('change', () => {
    filterState.confidence = confidenceFilter.value;
    refreshListVisibility();
});

reviewFilter?.addEventListener('change', () => {
    filterState.reviewStatus = reviewFilter.value;
    refreshListVisibility();
});

hideRejectedFilter?.addEventListener('change', () => {
    filterState.hideRejected = hideRejectedFilter.checked;
    refreshListVisibility();
});

searchInput?.addEventListener('input', refreshListVisibility);

reviewerNameInput?.addEventListener('change', () => {
    persistIdentityFields();
});

callSignInput?.addEventListener('change', () => {
    persistIdentityFields();
});

exportReviewBtn?.addEventListener('click', () => {
    reviewStore.exportReview();
    setStatus('Exported review JSON');
});

exportConfirmedBtn?.addEventListener('click', () => {
    reviewStore.exportConfirmed(collectCandidates());
    setStatus('Exported confirmed candidates');
});

exportRejectedBtn?.addEventListener('click', () => {
    reviewStore.exportRejected(collectCandidates());
    setStatus('Exported rejected candidates');
});

importReviewInput?.addEventListener('change', async () => {
    const file = importReviewInput.files?.[0];
    if (!file) {
        return;
    }

    try {
        const text = await file.text();
        const payload = JSON.parse(text);
        reviewStore.importFile(payload);
        applyReviewsToAll();
        if (payload.reviewer) {
            reviewerNameInput.value = payload.reviewer;
        }
        setStatus(`Imported review file (${reviewStore.entries.size} entries)`);
    } catch (err) {
        setStatus(`Import failed: ${err.message}`, true);
    } finally {
        importReviewInput.value = '';
    }
});

async function init() {
    setStatus('Loading Pennsylvania research layers...');

    reviewStore.loadFromLocalStorage();
    loadIdentityFields();
    pinStore.loadFromLocalStorage();
    refreshPinsUI();
    await reviewStore.loadFromUrl(`./data/${LOC_ID}/review/candidate-review-${LOC_ID}.json`);

    try {
        const stats = await research.loadAll(LOC_ID);
        buildCandidateList(collectCandidates());
        await refreshCommunityRejections();

        map.getView().fit(paExtent, { padding: [40, 380, 40, 40], duration: 0 });

        initParkSearch();

        const reviewedCount = reviewStore.entries.size;
        const rejectionCount = communityRejectionMap.size;
        setStatus([
            `${stats.candidates2} two-fer · ${stats.candidatesN} three-fer+ candidates`,
            `${rejectionCount} shared rejections on map`,
            `${reviewedCount} local reviews`,
        ].join(' — '));

        setInterval(() => {
            refreshCommunityRejections().catch(() => {});
        }, 60_000);
    } catch (err) {
        console.error(err);
        setStatus(err.message, true);
    }
}

init();
